import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, ArrowRight } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { useLanguage } from '@/i18n/LanguageContext';
import { blogArticles } from '@/i18n/blogArticles';
import { JsonLd } from '@/components/JsonLd';

export default function Blog() {
  const navigate = useNavigate();
  const { language } = useLanguage();

  const articles = blogArticles[language] || blogArticles.fr;

  return (
    <>
      <Helmet>
        <title>Blog | A-More AI - Conseils Rencontres & IA</title>
        <meta name="description" content="Découvrez nos articles sur les rencontres en ligne, l'intelligence artificielle, la psychologie amoureuse et des conseils pratiques pour trouver l'amour." />
        <meta name="keywords" content="blog rencontre, conseils dating, IA amour, psychologie couple, premier rendez-vous, compatibilité amoureuse" />
        <link rel="canonical" href={`${window.location.origin}/${language}/blog`} />
      </Helmet>

      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Blog",
        "name": "Blog A-More AI",
        "url": `${window.location.origin}/${language}/blog`,
        "inLanguage": language,
        "publisher": {
          "@type": "Organization",
          "name": "A-More AI",
          "logo": {
            "@type": "ImageObject",
            "url": `${window.location.origin}/logo.png`
          }
        },
        "blogPost": articles.map((article) => ({
          "@type": "BlogPosting",
          "headline": article.title,
          "description": article.excerpt,
          "image": article.image,
          "datePublished": article.date,
          "articleSection": article.category,
          "url": `${window.location.origin}/${language}/blog/${article.slug}`
        }))
      }} />

      <div className="min-h-screen py-8 px-6">
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-gray-600 hover:text-pink-600 mb-6 transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour
          </button>

          <div className="glass-card p-8 mb-8">
            <h1 className="text-4xl font-bold gradient-text mb-4">Blog A-More AI</h1>
            <p className="text-xl text-gray-600">
              Conseils, astuces et insights sur les rencontres, l'intelligence artificielle et les relations amoureuses
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {articles.map((article) => (
              <article
                key={article.slug}
                className="glass-card overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => navigate(`/${language}/blog/${article.slug}`)}
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={article.image}
                    alt={article.title}
                    className="w-full h-full object-cover transition-transform hover:scale-105 duration-300"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1 bg-pink-500 text-white text-xs font-semibold rounded-full">
                      {article.category}
                    </span>
                  </div>
                </div>

                <div className="p-6">
                  <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {article.date}
                    </div>
                    <div className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      {article.readTime}
                    </div>
                  </div>

                  <h2 className="text-xl font-bold text-gray-800 mb-3 line-clamp-2">
                    {article.title}
                  </h2>

                  <p className="text-gray-600 mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>

                  <div className="flex items-center text-pink-600 font-medium hover:text-pink-700 transition-colors">
                    Lire l'article
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </>
  );
}
